import { generate } from "rxjs";
import {
  CreateKbFolderInput,
  GetKbFolderContentsQueryProps,
  GetQueryProps,
  KbFileData,
  KbFileDetailResponse,
  KbFolderData,
  KbImportStatusResponse,
  KbRecordsOperationData,
  KbShareableLink,
  SendKbEmailInput,
  ToggleKbRecordsStarredStatusData,
} from "shared";
import { axiosClient } from "utilz";
import axiosUploadCall from "utilz/axios-call";
import {
  apiErrorHandler,
  generateUrlString,
  getQueryDataObject,
} from "utilz/helpers";

export const createKbFolder = async ({
  folderName,
  parentId,
}: CreateKbFolderInput) => {
  const url = "/v1/kb/folders";
  try {
    const response = await axiosClient.post(`${url}`, {
      name: folderName,
      parent_id: parentId,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getKbFolderDetails = async (folderId: string) => {
  const url = `/v1/kb/folders/${folderId}`;
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateKbFolder = async ({
  folderId,
  folderData,
}: {
  folderId: string;
  folderData: Partial<KbFolderData>;
}) => {
  const url = `/v1/kb/folders/${folderId}`;
  try {
    const response = await axiosClient.patch(`${url}`, folderData);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getKbFolderContents = async ({
  folderId,
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetKbFolderContentsQueryProps) => {
  const data = await getQueryDataObject({
    perPageCount,
    pageNumber,
    sortColumn,
    sortDirection,
    filter,
    searchQuery,
  });
  const url = await generateUrlString({
    baseUrl: `/v1/kb/folders/${folderId}/contents`,
    data,
  });
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

function uint8ToBase64(u8: Uint8Array) {
  // Avoids "spread" stack issues on large updates
  let s = "";
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    s += String.fromCharCode(...u8.subarray(i, i + chunk));
  }
  return btoa(s);
}

export const createKbFile = async (fileData: KbFileData) => {
  const url = "/v1/kb/files";
  try {
    const updateBase64 = uint8ToBase64(
      fileData?.yjs_update || new Uint8Array(),
    );
    console.log({
      ...fileData,
      yjs_update: updateBase64,
    });
    const response = await axiosClient.post(`${url}`, {
      ...fileData,
      yjs_update: updateBase64,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getKbFileDetails = async (
  fileId: string,
): Promise<KbFileDetailResponse> => {
  const url = `/v1/kb/files/${fileId}`;
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

export const getLiveKbFileDetails = async (
  articleId: string,
): Promise<KbFileDetailResponse> => {
  const url = `/v1/kb/files/${articleId}/live`;
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

export const updateKbFile = async ({
  fileId,
  fileData,
}: {
  fileId: string;
  fileData: Partial<KbFileData> & { raw_text?: string };
}) => {
  const url = `/v1/kb/files/${fileId}`;
  try {
    const response = await axiosClient.patch(`${url}`, fileData);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const saveSnapshotToKbFile = async ({
  articleId,
  snapshotId,
  fileData,
}: {
  articleId: string;
  snapshotId: string;
  fileData: Partial<KbFileData> & { raw_text?: string };
}) => {
  const url = `/v1/kb/files/${articleId}/snapshot/${snapshotId}`;
  try {
    const response = await axiosClient.post(`${url}`, fileData);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const autosaveKbFile = async ({
  fileData,
  snapshotId,
}: {
  fileData: Partial<KbFileData> & { record_id: string };
  snapshotId: string;
}) => {
  const url = `/v1/kb/files/${snapshotId}/snapshot`;
  try {
    const response = await axiosClient.patch(`${url}`, fileData);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getAutosavedKbFile = async (
  recordId: string,
): Promise<KbFileDetailResponse> => {
  const url = `/v1/kb/records/edit/${recordId}`;
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

export const deleteKbRecords = async (recordIds: string[]) => {
  const url = "/v1/kb/records/delete";
  try {
    const response = await axiosClient.patch(`${url}`, {
      ids: recordIds,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getAllKbRecords = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  const data = await getQueryDataObject({
    perPageCount,
    pageNumber,
    sortColumn,
    sortDirection,
    filter,
    searchQuery,
  });
  const url = await generateUrlString({
    baseUrl: "/v1/kb/files",
    data,
  });
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const kbFindPath = async (recordId: string) => {
  const url = `/v1/kb/records/${recordId}/path`;
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const moveKbRecords = async (data: KbRecordsOperationData) => {
  const url = "/v1/kb/records/move";
  try {
    const response = await axiosClient.put(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const toggleKbRecordsStarredStatus = async (
  data: ToggleKbRecordsStarredStatusData,
) => {
  const url = "/v1/kb/records/star";
  try {
    const response = await axiosClient.patch(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getKbStarredRecords = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  const data = await getQueryDataObject({
    perPageCount,
    pageNumber,
    sortColumn,
    sortDirection,
    filter,
    searchQuery,
  });
  const url = await generateUrlString({
    baseUrl: "/v1/kb/records/starred",
    data,
  });
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getKbRecentlyViewed = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  const data = await getQueryDataObject({
    perPageCount,
    pageNumber,
    sortColumn,
    sortDirection,
    filter,
    searchQuery,
  });
  const url = await generateUrlString({
    baseUrl: "/v1/kb/records/viewed/at",
    data,
  });
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateViewedAt = async (recordId: string) => {
  const url = "/v1/kb/records/viewed/at";
  try {
    const response = await axiosClient.patch(`${url}`, {
      record_id: recordId,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getKbDirectoryTree = async () => {
  const url = "/v1/kb/records/directory/tree";
  try {
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const uploadKbAttachments = ({ formData }: { formData: FormData }) =>
  axiosUploadCall("/v1/kb/records/upload/file", formData);

export const uploadKbFiles = ({
  files,
  folderId,
  onProgress,
}: {
  files: File[];
  folderId?: string | null;
  onProgress?: (progress: number, uploaded: number, total: number) => void;
}) => {
  const formData = new FormData();

  // Append all files to FormData
  files.forEach((file) => {
    formData.append("files", file);
  });

  // Append folder ID if provided
  if (folderId) {
    formData.append("parent_id", folderId);
  }

  // Create upload observable
  const uploadSubject = axiosUploadCall(
    "/v1/kb/import/documents/async",
    formData,
  );

  // Track progress if callback provided
  if (onProgress) {
    uploadSubject.subscribe({
      next: (event: any) => {
        if (event.status === "UPLOADING" && event.response) {
          const progress = event.percentage || 0;
          const uploaded = event.response.loaded || 0;
          const total = event.response.total || 0;
          onProgress(progress, uploaded, total);
        }
      },
    });
  }

  return uploadSubject;
};

// ============================================================================
// Collaboration API Functions
// ============================================================================

export const joinCollabSession = async ({
  mappingId,
  kbRecordId,
  userId,
  mspId,
  stateVector,
}: {
  mappingId: string;
  kbRecordId: string;
  userId: string;
  mspId: string;
  stateVector?: number[];
}) => {
  console.log("joinCollabSession", mappingId);
  const url = `/v1/kb/collab/${mappingId}/join`;
  if (!mappingId) return;
  try {
    const response = await axiosClient.post(url, {
      kb_record_id: kbRecordId,
      user_id: userId,
      msp_id: mspId,
      update: stateVector,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const leaveCollabSession = async ({
  mappingId,
  userId,
  mspId,
}: {
  mappingId: string;
  userId: string;
  mspId: string;
}) => {
  const url = `/v1/kb/collab/${mappingId}/leave`;
  try {
    const response = await axiosClient.post(url, {
      user_id: userId,
      msp_id: mspId,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateCollabCursor = async ({
  mappingId,
  userId,
  mspId,
  cursor,
}: {
  mappingId: string;
  userId: string;
  mspId: string;
  cursor: { anchor: number; head: number };
}) => {
  const url = `/v1/kb/collab/${mappingId}/cursor`;
  try {
    const response = await axiosClient.patch(url, {
      user_id: userId,
      msp_id: mspId,
      cursor,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const syncCollabState = async ({
  mappingId,
  userId,
  mspId,
  stateVector,
}: {
  mappingId: string;
  userId: string;
  mspId: string;
  stateVector: number[];
}) => {
  const url = `/v1/kb/collab/${mappingId}/sync`;
  try {
    const response = await axiosClient.post(url, {
      user_id: userId,
      msp_id: mspId,
      update: stateVector,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const collabSyncStateUpdate = async ({
  mappingId,
  userId,
  mspId,
  update,
}: {
  mappingId: string;
  userId: string;
  mspId: string;
  update: number[];
}) => {
  // const url = `/v1/kb/collab/${mappingId}/update`;
  const url = `/v1/kb/collab/${mappingId}/stream-update`;
  try {
    const response = await axiosClient.post(
      url,
      {
        user_id: userId,
        msp_id: mspId,
        update: update,
      },
      {
        headers: { "Content-Type": "application/octet-stream" },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      },
    );
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const saveCollabSnapshot = async ({
  mappingId,
  userId,
  mspId,
  stateVector,
  update,
}: {
  mappingId: string;
  userId: string;
  mspId: string;
  stateVector: number[];
  update: number[];
}) => {
  const url = `/v1/kb/collab/${mappingId}/save`;
  try {
    const response = await axiosClient.post(url, {
      user_id: userId,
      msp_id: mspId,
      state_vector: stateVector,
      update,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getCollabActiveUsers = async (
  mappingId: string,
  mspId: string,
) => {
  const url = `/v1/kb/collab/${mappingId}/users?msp_id=${mspId}`;
  try {
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const restoreOldVersion = async (versionMappingId: string) => {
  const url = `/v1/kb/files/${versionMappingId}/restore`;
  try {
    const response = await axiosClient.patch(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const unpublishKbFile = async (fileId: string) => {
  const url = `/v1/kb/files/${fileId}/unpublish`;
  try {
    const response = await axiosClient.patch(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const publishKbFileSnapshot = async (articleId: string) => {
  const url = `/v1/kb/files/${articleId}/snapshot/publish`;
  try {
    const response = await axiosClient.patch(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const createSnapshotFromRecord = async (recordId: string) => {
  const url = `/v1/kb/files/${recordId}/record-to-snapshot`;
  try {
    const response = await axiosClient.post(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

// ============================================================================
// Shareable Links API Functions
// ============================================================================

/**
 * Create or retrieve a shareable link for an article
 * @param articleId - The article ID
 * @returns Shareable link data
 */
export const createShareableLink = async (articleId: string) => {
  const url = `/v1/kb/shareable-links/${articleId}`;
  try {
    const response = await axiosClient.post(url, {});
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

/**
 * Get shareable link details by article ID
 * @param articleId - The article ID
 * @returns Shareable link data
 */
export const getShareableLink = async (
  articleId: string,
): Promise<KbShareableLink | undefined> => {
  const url = `/v1/kb/shareable-links/${articleId}`;
  try {
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data as KbShareableLink;
    }
    throw response;
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

/**
 * Toggle shareable link active status
 * @param articleId - The article ID
 * @param isActive - New active status
 * @returns Updated shareable link data
 */
export const toggleShareableLink = async (
  articleId: string,
  isActive: boolean,
) => {
  const url = `/v1/kb/shareable-links/${articleId}/toggle`;
  try {
    const response = await axiosClient.patch(url, { is_active: isActive });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

/**
 * Get shared article (Public API - No authentication required)
 * @param mappingId - The shareable link mapping ID
 * @returns Public article data
 */
export const getSharedArticle = async (mappingId: string) => {
  const url = `/v1/kb/public/shared/${mappingId}`;
  try {
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

/**
 * Get import jobs status for KB file uploads
 * @returns Import status data including total files, completed, failed, and pending counts
 */
export const getKbImportJobsStatus = async (): Promise<
  KbImportStatusResponse | undefined
> => {
  const url = "/v1/kb/import/jobs/status";
  try {
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

// ============================================================================
// Email API Functions
// ============================================================================

/**
 * Send KB article via email
 * @param data - Email data including subject, recipients, and html body
 * @returns Success response
 */
export const sendKbEmail = async (data: SendKbEmailInput) => {
  const url = "/v1/kb/email";
  try {
    const response = await axiosClient.post(url, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};
